import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLocalePath } from './LanguageRoute'
import logoSvg from '../../assets/gcg-logo.svg'

interface PaymentLogo {
  id: string
  label: string
  src: string
}

// Mollie hosts these SVGs for merchant use — see mollie.com/en/payment-method-logos.
// If the CDN becomes a problem, mirror under /payment-logos/ and switch the src.
const PAYMENT_LOGOS: PaymentLogo[] = [
  { id: 'ideal', label: 'iDEAL', src: 'https://www.mollie.com/external/icons/payment-methods/ideal.svg' },
  // Mollie publishes no per-brand `visa.svg` / `mastercard.svg` (both 404) — it
  // serves one generic card icon. Use it as the Visa/Mastercard trust mark.
  { id: 'creditcard', label: 'Visa / Mastercard', src: 'https://www.mollie.com/external/icons/payment-methods/creditcard.svg' },
  { id: 'paypal', label: 'PayPal', src: 'https://www.mollie.com/external/icons/payment-methods/paypal.svg' },
  { id: 'bancontact', label: 'Bancontact', src: 'https://www.mollie.com/external/icons/payment-methods/bancontact.svg' },
  { id: 'applepay', label: 'Apple Pay', src: 'https://www.mollie.com/external/icons/payment-methods/applepay.svg' },
  { id: 'klarna', label: 'Klarna', src: 'https://www.mollie.com/external/icons/payment-methods/klarna.svg' },
]

export default function SiteFooter() {
  const lp = useLocalePath()
  const { t } = useTranslation()
  const year = new Date().getFullYear()

  return (
    <footer className="bg-gcg-dark border-t-[3px] border-gcg-orange mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">

        {/* 4-column grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1.1fr] gap-10">

          {/* Col 1 — Brand + contact */}
          <div>
            <Link to={lp('/')} className="inline-flex items-center gap-2 mb-6" aria-label="Global Control Group Store">
              <img src={logoSvg} alt="" aria-hidden="true" className="h-8 w-auto" />
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="text-[15px] font-bold text-white tracking-[-0.01em] whitespace-nowrap">Global Control Group</span>
                <span className="text-[15px] font-extrabold text-gcg-orange tracking-[-0.01em] whitespace-nowrap">{t('shop.footer.brandSub')}</span>
              </div>
            </Link>
            <address className="not-italic flex flex-col gap-1.5 text-sm text-[#D4D2C9]">
              <span className="text-white font-semibold">Global Control Group B.V.</span>
              <span>Julianaweg 200</span>
              <span>1131 DH Volendam — Nederland</span>
              <a href="tel:+31299705078" className="hover:text-white transition-colors">T: +31 299 705 078</a>
              <a href="mailto:info@globalcontrol.group" className="hover:text-white transition-colors">info@globalcontrol.group</a>
            </address>
            <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs text-[#B4B2A9]">
              <dt className="font-semibold">KvK</dt><dd>88795892</dd>
              <dt className="font-semibold">BTW</dt><dd>NL864662430B01</dd>
              <dt className="font-semibold">Openingstijden</dt><dd>Ma–Vr 08:30–17:00 CET</dd>
              <dt className="font-semibold">Reactie</dt><dd>binnen 1 werkdag</dd>
            </dl>
          </div>

          {/* Col 2 — Producten */}
          <div>
            <h3 className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#B4B2A9] mb-3.5">{t('shop.footerLinks.products')}</h3>
            <nav className="flex flex-col">
              <Link to={lp('/products')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footer.allProducts')}</Link>
              <Link to={lp('/products?category=X20+System')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">X20 System</Link>
              <Link to={lp('/products?category=HMI+%26+Panel+PCs')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">HMI &amp; Panel PCs</Link>
              <Link to={lp('/products?category=Motion+Control')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">Motion Control</Link>
              <Link to={lp('/products?category=I%2FO+Modules')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">I/O Modules</Link>
            </nav>
          </div>

          {/* Col 3 — Klantenservice */}
          <div>
            <h3 className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#B4B2A9] mb-3.5">{t('shop.footer.serviceCol')}</h3>
            <nav className="flex flex-col">
              <Link to={lp('/contact')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footerLinks.contact')}</Link>
              <Link to={lp('/cms/levering-en-retour')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footerLinks.returns')}</Link>
              <Link to={lp('/cms/herroepingsrecht')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footerLinks.withdrawal')}</Link>
              <Link to={lp('/cms/veelgestelde-vragen')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footerLinks.faq')}</Link>
              <Link to={lp('/account')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footerLinks.account')}</Link>
            </nav>
          </div>

          {/* Col 4 — Bedrijf & juridisch */}
          <div>
            <h3 className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#B4B2A9] mb-3.5">{t('shop.footer.generalCol')}</h3>
            <nav className="flex flex-col">
              <Link to={lp('/about')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footerLinks.about')}</Link>
              <a href="https://globalcontrol.group" target="_blank" rel="noopener noreferrer" className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footer.mainSite')}</a>
              <Link to={lp('/cms/algemene-voorwaarden')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footerLinks.terms')}</Link>
              <Link to={lp('/cms/privacyverklaring')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footerLinks.privacy')}</Link>
              <Link to={lp('/cms/cookiebeleid')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footerLinks.cookies')}</Link>
              <Link to={lp('/cms/wettelijke-kennisgeving')} className="text-sm text-white py-1 hover:text-gcg-orange transition-colors">{t('shop.footerLinks.imprint')}</Link>
            </nav>
          </div>
        </div>

        {/* Payment + security strip */}
        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <div>
            <h4 className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#B4B2A9] mb-2">{t('shop.footer.paymentTitle')}</h4>
            <ul className="flex flex-wrap items-center gap-2" aria-label={t('shop.footer.paymentTitle') as string}>
              {PAYMENT_LOGOS.map(p => (
                <li key={p.id} className="inline-flex items-center justify-center h-8 px-2 bg-white rounded">
                  <img
                    src={p.src}
                    alt={p.label}
                    loading="lazy"
                    width="38"
                    height="24"
                    className="h-6 w-auto"
                  />
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-[#B4B2A9]">{t('shop.footer.paymentNote')}</p>
          </div>
          <div className="text-xs text-[#B4B2A9] lg:text-right max-w-xs">
            <p className="font-semibold text-white">{t('shop.footer.complianceTitle')}</p>
            <p className="mt-1">{t('shop.footer.complianceBody')}</p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 text-[11px] font-medium tracking-[0.08em] uppercase text-[#B4B2A9]">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{t('shop.footer.copyright', { year })}</span>
            <span aria-hidden="true">·</span>
            <span>{t('shop.footer.brTag')}</span>
          </div>
          <nav className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to={lp('/cookie-instellingen')} className="hover:text-white transition-colors">{t('shop.footerLinks.cookiePreferences')}</Link>
            <a href="/.well-known/security.txt" className="hover:text-white transition-colors" rel="noopener">security.txt</a>
          </nav>
        </div>

      </div>
    </footer>
  )
}
